/**
 * Reads/writes the active ticker in the URL hash so it's bookmarkable
 * and survives reloads. Falls back to a default when the hash is empty
 * or doesn't look like a valid ticker symbol.
 *
 * Open-universe contract (mirrors backend `_is_admissible_ticker`):
 * any symbol matching `^[A-Z][A-Z0-9.\-]{0,9}$` is accepted, not
 * just the always-on pill list. The `_available` arg is preserved
 * for caller API stability but is no longer consulted for validation
 * — it's effectively documentation now.
 *
 * Was a bug: previously this hook rejected anything outside
 * `available`, silently dropping setTicker('NVDA') calls from the
 * SymbolSearch component. The search bar would appear to do nothing
 * and the page would stay pinned to whatever pill was last clicked.
 */

import { useEffect, useState, useCallback } from 'react';

// Same shape as backend regex in api/rest.py::_TICKER_REGEX.
// Letter-led, 1-10 chars, allows . and - for tickers like BRK.B.
const VALID_SYMBOL = /^[A-Z][A-Z0-9.\-]{0,9}$/;

function readHash(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hash.slice(1).toUpperCase();
}

function isValidSymbol(s: string): boolean {
  return VALID_SYMBOL.test(s);
}

export function useHashTicker(_available: string[], fallback: string) {
  const [ticker, setTicker] = useState<string>(() => {
    const h = readHash();
    if (h && isValidSymbol(h)) return h;
    return fallback;
  });

  // React to manual hash changes (browser back/forward, or our own
  // history.replaceState() write below). Some browsers don't fire
  // hashchange on replaceState() — the `setTicker(next)` call inside
  // updateTicker handles state directly, so we don't depend on the
  // event firing for the happy-path search-and-pick flow.
  useEffect(() => {
    const onHash = () => {
      const h = readHash();
      if (h && isValidSymbol(h) && h !== ticker) setTicker(h);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [ticker]);

  const updateTicker = useCallback((next: string) => {
    if (!isValidSymbol(next)) return;
    history.replaceState(null, '', `#${next}`);
    setTicker(next);
  }, []);

  return [ticker, updateTicker] as const;
}
